import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { API } from "@/App";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Search, X, Send, Loader2, CheckCircle2, XCircle } from "lucide-react";

export function ManualSendAutomationDialog({ automation, onClose }) {
  const [search, setSearch] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selected, setSelected] = useState([]);
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  const debounceRef = useRef(null);

  useEffect(() => {
    setSearch("");
    setResults([]);
    setSelected([]);
    setResult(null);
  }, [automation]);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!search.trim()) {
      setResults([]);
      return;
    }
    debounceRef.current = setTimeout(async () => {
      setSearching(true);
      try {
        const res = await axios.get(`${API}/clients`, { params: { search, limit: 20 } });
        setResults(res.data.clients || []);
      } catch (e) { /* ignore */ }
      setSearching(false);
    }, 300);
    return () => clearTimeout(debounceRef.current);
  }, [search]);

  const toggle = (client) => {
    setSelected((prev) => (prev.find((c) => c.id === client.id) ? prev.filter((c) => c.id !== client.id) : [...prev, client]));
  };

  const send = async () => {
    if (selected.length === 0) return;
    setSending(true);
    setResult(null);
    try {
      const res = await axios.post(`${API}/automations/${automation.id}/send-manual`, {
        client_ids: selected.map((c) => c.id),
      });
      if (res.data.error) {
        toast.error(res.data.error);
      } else {
        setResult(res.data);
        if (res.data.sent_count > 0) toast.success(`Sent to ${res.data.sent_count} client${res.data.sent_count !== 1 ? "s" : ""}`);
        if (res.data.failed_count > 0) toast.error(`${res.data.failed_count} failed to send — see details below`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to send");
    }
    setSending(false);
  };

  if (!automation) return null;

  return (
    <Dialog open={!!automation} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="rounded-2xl max-w-lg max-h-[85vh] overflow-y-auto" data-testid="manual-send-dialog">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: "Playfair Display" }}>Send "{automation.name}" to specific clients</DialogTitle>
        </DialogHeader>

        <div className="space-y-3">
          <p className="text-xs text-muted-foreground">
            Pick one or more clients to send this email to right now. This bypasses the usual cooldown / eligibility checks —
            it always sends when you choose the client yourself.
          </p>

          {selected.length > 0 && (
            <div className="flex flex-wrap gap-1.5" data-testid="manual-send-selected-chips">
              {selected.map((c) => (
                <Badge key={c.id} variant="outline" className="rounded-full text-xs pl-2.5 pr-1 py-1 gap-1 flex items-center">
                  {c.name}
                  <button type="button" onClick={() => toggle(c)} className="hover:text-destructive ml-0.5" data-testid={`manual-send-remove-${c.id}`}>
                    <X size={11} />
                  </button>
                </Badge>
              ))}
            </div>
          )}

          <div className="relative">
            <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search clients by name, email or phone..."
              className="pl-8 rounded-xl"
              data-testid="manual-send-client-search"
            />
          </div>

          <div className="border rounded-xl max-h-56 overflow-y-auto divide-y">
            {searching && <p className="text-xs text-muted-foreground p-3 text-center">Searching…</p>}
            {!searching && search.trim() && results.length === 0 && (
              <p className="text-xs text-muted-foreground p-3 text-center">No clients found</p>
            )}
            {!searching &&
              results.map((c) => {
                const isSel = selected.some((s) => s.id === c.id);
                return (
                  <button
                    key={c.id}
                    type="button"
                    onClick={() => toggle(c)}
                    data-testid={`manual-send-client-row-${c.id}`}
                    className={`w-full text-left p-2.5 flex items-center justify-between gap-2 hover:bg-muted/40 transition-colors ${isSel ? "bg-primary/5" : ""}`}
                  >
                    <div className="min-w-0">
                      <p className="text-sm font-medium truncate">{c.name}</p>
                      <p className="text-xs text-muted-foreground truncate">{c.email || "No email on file"}</p>
                    </div>
                    {isSel && <CheckCircle2 size={16} className="text-primary shrink-0" />}
                  </button>
                );
              })}
            {!search.trim() && <p className="text-xs text-muted-foreground p-3 text-center">Start typing to search clients</p>}
          </div>

          {result && (
            <div className="space-y-1.5 border-t pt-2" data-testid="manual-send-result">
              {result.sent?.map((s) => (
                <div key={s.client_id} className="flex items-center gap-1.5 text-xs text-emerald-700">
                  <CheckCircle2 size={13} className="shrink-0" /> Sent to {s.client_name || s.email}
                </div>
              ))}
              {result.failed?.map((f) => (
                <div key={f.client_id} className="flex items-center gap-1.5 text-xs text-red-600">
                  <XCircle size={13} className="shrink-0" /> {f.client_name || f.client_id}: {f.reason}
                </div>
              ))}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={onClose} className="rounded-xl">Close</Button>
          <Button
            onClick={send}
            disabled={selected.length === 0 || sending}
            className="rounded-xl gap-1.5"
            data-testid="manual-send-submit-btn"
          >
            {sending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
            {sending ? "Sending…" : `Send to ${selected.length || ""} client${selected.length === 1 ? "" : "s"}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
